/**
 * ZAXIA Staff Login Page
 * Professional authentication interface for hotel staff
 *
 * Phase P2.1-3: Now uses AuthContext for login
 * No direct localStorage access - all through useAuth hook
 */

import { useState, useCallback, FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';

export function LoginPage() {
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        await login(email, password);
        // No need to call onLoginSuccess - context state change triggers re-render
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed');
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, login]
  );

  return (
    <div className="min-h-screen bg-zaxia-dark flex items-center justify-center p-4">
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-zaxia-teal/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      {/* Login Card */}
      <div className="relative w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold tracking-wider mb-2">
            <span className="bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
              ZAXIA
            </span>
          </h1>
          <p className="text-zaxia-gray text-sm tracking-widest uppercase">
            Staff Portal
          </p>
        </div>

        {/* Card */}
        <div className="bg-zaxia-dark-light/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            Sign In
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-zaxia-gray-light mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="staff@hotel.com"
                required
                autoComplete="email"
                disabled={isLoading}
                className="w-full px-4 py-3 bg-zaxia-dark/60 border border-white/10 rounded-lg 
                           text-white placeholder-zaxia-gray/50
                           focus:outline-none focus:ring-2 focus:ring-zaxia-teal/50 focus:border-zaxia-teal
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200"
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-zaxia-gray-light mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                disabled={isLoading}
                className="w-full px-4 py-3 bg-zaxia-dark/60 border border-white/10 rounded-lg 
                           text-white placeholder-zaxia-gray/50
                           focus:outline-none focus:ring-2 focus:ring-zaxia-teal/50 focus:border-zaxia-teal
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full py-3 px-4 mt-2
                         bg-gradient-to-r from-zaxia-teal to-cyan-600
                         hover:from-zaxia-teal-light hover:to-cyan-500
                         disabled:from-zaxia-teal/50 disabled:to-cyan-600/50 disabled:cursor-not-allowed
                         text-white font-semibold rounded-lg
                         shadow-lg shadow-zaxia-teal/20
                         transform transition-all duration-200
                         hover:scale-[1.02] active:scale-[0.98]
                         focus:outline-none focus:ring-2 focus:ring-zaxia-teal/50"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-zaxia-gray/60 text-xs mt-6">
          © 2026 ZAXIA Smart Hotel Concierge
        </p>
      </div>
    </div>
  );
}
